import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash, Buildings, CaretDown, CaretRight, Pencil, FloppyDisk, FilePdf, FileArrowUp, DownloadSimple, Paperclip } from "@phosphor-icons/react";
import { toast } from "sonner";

function fmtBytes(n) {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const MAX_DOC_BYTES = 15 * 1024 * 1024;

function SupplierDocs({ ingredientId, supplier, onChanged }) {
  const docs = supplier.docs || [];
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX_DOC_BYTES) {
      toast.error("File too large (max 15 MB)");
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(
        `/ingredients/${ingredientId}/suppliers/${supplier.id}/docs`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success("Document uploaded");
      onChanged();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(
        `/ingredients/${ingredientId}/suppliers/${supplier.id}/docs/${doc.id}/download`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.content_type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Remove "${doc.filename}"?`)) return;
    try {
      await api.delete(`/ingredients/${ingredientId}/suppliers/${supplier.id}/docs/${doc.id}`);
      toast.success("Document removed");
      onChanged();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="mt-2 pt-2 border-t border-stone-200" data-testid={`supplier-docs-${supplier.id}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-1">
        <Paperclip size={11} weight="bold" /> Documents (COA / spec sheet)
      </div>
      {docs.length > 0 && (
        <div className="space-y-1 mb-1.5">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-1.5 text-[11px] bg-white rounded border border-stone-200 px-2 py-1" data-testid={`doc-${d.id}`}>
              <FilePdf size={13} className="text-rose-500 shrink-0" weight="duotone" />
              <button onClick={() => download(d)} className="flex-1 min-w-0 text-left truncate text-stone-700 hover:text-brand-700 hover:underline" title={d.filename}>
                {d.filename}
              </button>
              <span className="text-stone-400 shrink-0">{fmtBytes(d.size)}</span>
              <button onClick={() => download(d)} className="text-stone-400 hover:text-brand-700 p-0.5 shrink-0" title="Download"><DownloadSimple size={12} /></button>
              <button onClick={() => remove(d)} className="text-stone-400 hover:text-rose-600 p-0.5 shrink-0" title="Remove"><Trash size={11} /></button>
            </div>
          ))}
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        className="hidden"
        accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.csv,.xls,.xlsx,.doc,.docx,.txt"
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid={`doc-input-${supplier.id}`}
      />
      <Button
        variant="outline" size="sm"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="w-full h-7 text-[11px] border-dashed border-stone-300 text-stone-600 hover:border-brand-500 hover:text-brand-800"
        data-testid={`upload-doc-btn-${supplier.id}`}
      >
        <FileArrowUp size={12} weight="bold" className="mr-1" />
        {uploading ? "Uploading…" : "Upload document"}
      </Button>
      <p className="text-[10px] text-stone-400 mt-1">PDF, image, Excel/CSV, Word, or text · up to 15 MB</p>
    </div>
  );
}

// Common spec keys food scientists track per supplier — pre-populated as suggestions
const COMMON_SPEC_KEYS = [
  { key: "Moisture", unit: "%" },
  { key: "Protein", unit: "%" },
  { key: "Total Fat", unit: "%" },
  { key: "Ash", unit: "%" },
  { key: "Salt", unit: "%" },
  { key: "ASTA Color", unit: "" },
  { key: "Mesh / Granulation", unit: "mesh" },
  { key: "Bulk Density", unit: "g/mL" },
  { key: "Total Plate Count", unit: "CFU/g" },
  { key: "Yeast & Mold", unit: "CFU/g" },
  { key: "Volatile Oil", unit: "% v/w" },
  { key: "Acidity (acetic eq)", unit: "%" },
];

function SpecRow({ spec, onChange, onRemove }) {
  return (
    <div className="grid grid-cols-12 gap-1 items-center" data-testid="supplier-spec-row">
      <Input
        placeholder="Spec (e.g., Moisture)"
        value={spec.key}
        onChange={(e) => onChange({ ...spec, key: e.target.value })}
        className="col-span-4 h-7 text-[12px] border-stone-300"
      />
      <Input
        type="number" step="0.01"
        placeholder="Min"
        value={spec.range_min ?? ""}
        onChange={(e) => onChange({ ...spec, range_min: e.target.value === "" ? null : Number(e.target.value) })}
        className="col-span-2 h-7 text-[12px] border-stone-300"
      />
      <span className="col-span-1 text-center text-stone-400 text-xs">–</span>
      <Input
        type="number" step="0.01"
        placeholder="Max"
        value={spec.range_max ?? ""}
        onChange={(e) => onChange({ ...spec, range_max: e.target.value === "" ? null : Number(e.target.value) })}
        className="col-span-2 h-7 text-[12px] border-stone-300"
      />
      <Input
        placeholder="Unit"
        value={spec.unit ?? ""}
        onChange={(e) => onChange({ ...spec, unit: e.target.value })}
        className="col-span-2 h-7 text-[12px] border-stone-300"
      />
      <button
        onClick={onRemove}
        className="col-span-1 h-7 grid place-items-center text-rose-500 hover:bg-rose-50 rounded"
        data-testid="remove-spec"
      >
        <Trash size={12} />
      </button>
    </div>
  );
}

function SupplierEditor({ ingredientId, supplier, onSaved, onCanceled }) {
  const [name, setName] = useState(supplier?.supplier_name || "");
  const [specs, setSpecs] = useState(supplier?.specs?.length ? supplier.specs : [{ key: "", range_min: null, range_max: null, unit: "" }]);
  const [pricePerLb, setPricePerLb] = useState(supplier?.price_per_lb ?? "");
  const [moqLb, setMoqLb] = useState(supplier?.moq_lb ?? "");
  const [leadTimeDays, setLeadTimeDays] = useState(supplier?.lead_time_days ?? "");
  const [notes, setNotes] = useState(supplier?.notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error("Supplier name required"); return; }
    setSaving(true);
    const payload = {
      supplier_name: name.trim(),
      specs: specs.filter((s) => s.key && s.key.trim()),
      price_per_lb: pricePerLb === "" ? null : Number(pricePerLb),
      moq_lb: moqLb === "" ? null : Number(moqLb),
      lead_time_days: leadTimeDays === "" ? null : Number(leadTimeDays),
      notes: notes.trim(),
    };
    try {
      if (supplier?.id) {
        await api.put(`/ingredients/${ingredientId}/suppliers/${supplier.id}`, payload);
        toast.success("Supplier updated");
      } else {
        await api.post(`/ingredients/${ingredientId}/suppliers`, payload);
        toast.success("Supplier added");
      }
      onSaved();
    } catch (err) {
      console.error(err);
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-md border border-brand-200 p-3 space-y-3" data-testid="supplier-editor">
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-stone-500">Supplier name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Frontier Co-op, Mountain Rose, Atlantic Spice…"
          className="h-8 mt-1 border-stone-300" data-testid="supplier-name-input" />
      </div>

      <div>
        <Label className="text-[10px] uppercase tracking-wider text-stone-500">Spec ranges</Label>
        <div className="grid grid-cols-12 gap-1 text-[10px] uppercase tracking-wider text-stone-400 font-bold mb-1 mt-1">
          <span className="col-span-4">Spec key</span>
          <span className="col-span-2 text-center">Min</span>
          <span className="col-span-1"></span>
          <span className="col-span-2 text-center">Max</span>
          <span className="col-span-2">Unit</span>
        </div>
        <div className="space-y-1">
          {specs.map((spec, i) => (
            <SpecRow
              key={i}
              spec={spec}
              onChange={(updated) => setSpecs((s) => s.map((x, idx) => idx === i ? updated : x))}
              onRemove={() => setSpecs((s) => s.filter((_, idx) => idx !== i))}
            />
          ))}
        </div>
        <div className="flex gap-2 mt-2 flex-wrap">
          <Button variant="ghost" size="sm" className="h-6 text-[11px]"
            onClick={() => setSpecs((s) => [...s, { key: "", range_min: null, range_max: null, unit: "" }])}
            data-testid="add-spec-row">
            <Plus size={10} weight="bold" className="mr-1" /> Add spec
          </Button>
          {COMMON_SPEC_KEYS.slice(0, 4).map((c) => (
            <button
              key={c.key}
              onClick={() => setSpecs((s) => [...s, { key: c.key, range_min: null, range_max: null, unit: c.unit }])}
              className="text-[10px] uppercase tracking-wider font-bold bg-stone-100 hover:bg-brand-100 hover:text-brand-800 text-stone-600 px-2 py-0.5 rounded"
            >
              + {c.key}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-stone-500">$ / lb</Label>
          <Input type="number" step="0.01" value={pricePerLb}
            onChange={(e) => setPricePerLb(e.target.value)}
            className="h-7 mt-1 border-stone-300 text-[12px]" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-stone-500">MOQ (lb)</Label>
          <Input type="number" step="1" value={moqLb}
            onChange={(e) => setMoqLb(e.target.value)}
            className="h-7 mt-1 border-stone-300 text-[12px]" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-stone-500">Lead time (days)</Label>
          <Input type="number" step="1" value={leadTimeDays}
            onChange={(e) => setLeadTimeDays(e.target.value)}
            className="h-7 mt-1 border-stone-300 text-[12px]" />
        </div>
      </div>

      <div>
        <Label className="text-[10px] uppercase tracking-wider text-stone-500">Notes</Label>
        <Input value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Drop-ship? Allergen-free facility? Kosher? Last quoted MM/YY?"
          className="h-7 mt-1 border-stone-300 text-[12px]" />
      </div>

      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={onCanceled} disabled={saving}>Cancel</Button>
        <Button size="sm" onClick={save} disabled={saving}
          className="bg-brand-800 hover:bg-brand-700 text-white"
          data-testid="save-supplier-btn">
          <FloppyDisk size={12} weight="bold" className="mr-1" /> {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

function SupplierCard({ supplier, ingredientId, onEdit, onDeleted, onChanged }) {
  const remove = async () => {
    if (!window.confirm(`Remove supplier "${supplier.supplier_name}"?`)) return;
    try {
      await api.delete(`/ingredients/${ingredientId}/suppliers/${supplier.id}`);
      toast.success("Supplier removed");
      onDeleted();
    } catch (err) {
      toast.error("Delete failed");
    }
  };
  return (
    <div className="bg-stone-50 rounded-md border border-stone-200 p-2.5" data-testid={`supplier-${supplier.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="font-semibold text-[13px] text-stone-800 flex items-center gap-1.5">
          <Buildings size={12} className="text-stone-500" weight="duotone" />
          {supplier.supplier_name}
        </div>
        <div className="flex gap-1">
          <button onClick={onEdit} className="text-stone-500 hover:text-brand-700 p-0.5"><Pencil size={11} /></button>
          <button onClick={remove} className="text-stone-500 hover:text-rose-600 p-0.5"><Trash size={11} /></button>
        </div>
      </div>
      {supplier.price_per_lb != null && (
        <div className="text-[11px] text-stone-600 mt-0.5">
          <b>${Number(supplier.price_per_lb).toFixed(2)}/lb</b>
          {supplier.moq_lb ? ` · MOQ ${supplier.moq_lb} lb` : ""}
          {supplier.lead_time_days ? ` · ${supplier.lead_time_days}-day lead` : ""}
        </div>
      )}
      {supplier.specs?.length > 0 && (
        <div className="mt-1.5 grid grid-cols-2 gap-x-2 gap-y-0.5">
          {supplier.specs.map((s, i) => (
            <div key={i} className="text-[11px]">
              <span className="text-stone-500">{s.key}: </span>
              <span className="font-mono text-stone-800">
                {s.range_min ?? "—"}{s.range_max != null && s.range_min != null ? "–" : ""}{s.range_max ?? ""}
                {s.unit ? ` ${s.unit}` : ""}
              </span>
            </div>
          ))}
        </div>
      )}
      {supplier.notes && (
        <div className="mt-1.5 text-[11px] text-stone-500 italic">{supplier.notes}</div>
      )}
      <SupplierDocs ingredientId={ingredientId} supplier={supplier} onChanged={onChanged} />
    </div>
  );
}

export default function SupplierSpecsPanel({ ingredientId }) {
  const [open, setOpen] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | spec_id

  const load = () => {
    api.get(`/ingredients/${ingredientId}/suppliers`)
      .then((r) => setSuppliers(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const editingSupplier = editing && editing !== "new"
    ? suppliers.find((s) => s.id === editing)
    : null;

  return (
    <div className="mt-2" data-testid={`supplier-panel-${ingredientId}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-stone-500 hover:text-brand-700 transition-colors"
        data-testid="supplier-panel-toggle"
      >
        {open ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
        Suppliers {suppliers.length > 0 && <span className="bg-brand-100 text-brand-800 px-1.5 rounded">{suppliers.length}</span>}
      </button>

      {open && (
        <div className="mt-2 space-y-2">
          {suppliers.map((s) => (
            editing === s.id ? (
              <SupplierEditor
                key={s.id}
                ingredientId={ingredientId}
                supplier={s}
                onSaved={() => { setEditing(null); load(); }}
                onCanceled={() => setEditing(null)}
              />
            ) : (
              <SupplierCard
                key={s.id}
                supplier={s}
                ingredientId={ingredientId}
                onEdit={() => setEditing(s.id)}
                onDeleted={load}
                onChanged={load}
              />
            )
          ))}

          {editing === "new" ? (
            <SupplierEditor
              ingredientId={ingredientId}
              onSaved={() => { setEditing(null); load(); }}
              onCanceled={() => setEditing(null)}
            />
          ) : (
            !editingSupplier && (
              <Button variant="outline" size="sm"
                onClick={() => setEditing("new")}
                className="w-full h-7 text-[11px] border-dashed border-stone-300 text-stone-600 hover:border-brand-500 hover:text-brand-800"
                data-testid="add-supplier-btn">
                <Plus size={11} weight="bold" className="mr-1" /> Add supplier
              </Button>
            )
          )}
        </div>
      )}
    </div>
  );
}
